// The Inbox (#495, #496, #497, #808): what is waiting for you to answer, across every
// workspace you hold — the one list of waiting requests (#795, decision 2).
//
// **One table per workspace, under a real heading**, in the route's order
// (`inbox.py::group_by_workspace`): the most pressing workspace first, and inside it the
// soonest deadline first. Every kind shares the columns — kind, subject, workspace,
// requested by, due — and what each kind puts in them is `inboxKinds.tsx`'s.
//
// **Filters, not screens.** Approvals and Data access were screens of their own; they are
// filters here, and their old paths open the filter (`inboxView.ts`). Data access also
// lists the selected workspace's standing grants, which lived on its screen.
//
// **Answering opens a drawer** (`InboxDrawer.tsx`) with the item's own controls, in the
// item's own context, and no page load. The subject is the row's button, and focus
// returns to it when the drawer closes.
//
// **An answered item stays** (#497), under its workspace, newest first: what the answer
// was, who gave it and when, and no button, because there is nothing left to answer.
// "Show older" reads the group's next page.

import { Fragment, type ReactNode } from "react";

import { groupKeyOf, type AnsweredItem, type InboxGroup, type InboxItem, type InboxRead } from "../api/inbox";
import type { ConsentRead } from "../api/consent";
import type { Membership } from "../api/memberships";
import type { ContextChoice, SessionResponse } from "../api/session";
import { Chip, Empty, Notice, PageIntro } from "./chrome";
import { ConsentPolicyControl, StandingGrants } from "./ConsentPage";
import { InboxDrawer, type DrawerActions, type DrawerSources } from "./InboxDrawer";
import { KindIcon, outcomeText, rowOf, whenOf, type RowItem } from "./inboxKinds";
import { INBOX_KINDS, filterLabel, inboxFilters, inboxPath, kindsOf, openKey, openOf, type InboxView } from "./inboxView";
import { readableTime } from "./runPresentation";
import { deadlineOf, isUrgent } from "./WaitingInvitations";

export function InboxPage({
  inbox,
  error,
  address,
  view = { filter: null, open: null },
  session,
  contextError = null,
  linkArrived = null,
  memberships = null,
  workspaceName = "this workspace",
  sources,
  actions,
  onOlder = () => {},
  go,
}: {
  /** The groups, or null while the read is in flight. */
  inbox: InboxRead | null;
  error: string | null;
  /** The signed-in address: what authorises an invitation's row (`WaitingInvitations`). */
  address: string;
  /** The filter and the drawer's item, as the location names them (`inboxView.ts`). */
  view?: InboxView;
  session: SessionResponse;
  contextError?: string | null;
  /** The context a link selected, once there: the drawer says the person has landed in it (#858). */
  linkArrived?: ContextChoice | null;
  /** The contexts the person holds, by name: what the drawer calls the item's (#858). */
  memberships?: Membership[] | null;
  /** The selected context's name, for the standing grants it holds. */
  workspaceName?: string;
  sources: DrawerSources;
  actions: DrawerActions;
  /** Read a group's next page of answered items: its key and the cursor it was handed. */
  onOlder?: (group: string, cursor: string) => void;
  /** Move the location without a page load (`location.ts::navigate`). */
  go: (path: string) => void;
}) {
  const { filter, open } = view;
  const shown = filter === null ? null : new Set(kindsOf(filter));
  const passes = (item: { kind: InboxItem["kind"] }) => shown === null || shown.has(item.kind);
  const groups = (inbox?.groups ?? [])
    .map((group) => ({ ...group, items: group.items.filter(passes), answered: group.answered.filter(passes) }))
    .filter((group) => group.items.length > 0 || group.answered.length > 0);
  const waiting = groups.reduce((n, group) => n + group.items.length, 0);
  const sameAs = (item: { kind: InboxItem["kind"]; id: string }) => open !== null && item.kind === open.kind && item.id === open.id;
  const openItem = inbox?.groups.flatMap((group) => group.items).find(sameAs) ?? null;
  // Already answered: the drawer says so, and by whom, rather than offering it (#858).
  const openAnswered = inbox?.groups.flatMap((group) => group.answered).find(sameAs) ?? null;
  const standing = filter === INBOX_KINDS.consent_request.filter && session.workspace_id !== null;

  return (
    <>
      <PageIntro
        destination="inbox"
        action={inbox === null ? null : <Chip tone={inbox.count > 0 ? "warn" : undefined}>{inbox.count} waiting</Chip>}
      />
      <Filters inbox={inbox} filter={filter} onFilter={(next) => go(inboxPath({ filter: next }))} />
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {inbox === null && error === null ? (
        <section className="panel">
          <p className="muted panel__body" role="status">
            Reading what is waiting for you…
          </p>
        </section>
      ) : null}
      {inbox !== null && waiting === 0 ? (
        <section className="panel">
          <Empty
            title={
              filter === null
                ? "Nothing is waiting for your answer."
                : `Nothing under ${filterLabel(filter)} is waiting for your answer.`
            }
          />
        </section>
      ) : null}
      {groups.map((group) => (
        <Group
          key={groupKeyOf(group)}
          group={group}
          onOpen={(item) => go(inboxPath({ filter, open: openOf(item) }))}
          onOlder={onOlder}
        />
      ))}
      {standing ? <ConsentPolicyControl session={session} /> : null}
      {standing ? <Standing consent={sources.consent} error={sources.consentError} workspaceName={workspaceName} /> : null}
      {open === null ? null : (
        <InboxDrawer
          key={openKey(open)}
          open={open}
          item={openItem}
          answered={openAnswered}
          filter={filter}
          session={session}
          contextError={contextError}
          linkArrived={linkArrived}
          memberships={memberships}
          sources={sources}
          actions={actions}
          address={address}
          onClose={() => go(inboxPath({ filter }))}
        />
      )}
    </>
  );
}

/** One button per filter, and All: pressed is the one the list is under. */
function Filters({
  inbox,
  filter,
  onFilter,
}: {
  inbox: InboxRead | null;
  filter: string | null;
  onFilter: (filter: string | null) => void;
}) {
  const count = (id: string | null) => {
    const kinds = id === null ? null : new Set(kindsOf(id));
    return (inbox?.groups ?? []).reduce(
      (n, group) => n + group.items.filter((item) => kinds === null || kinds.has(item.kind)).length,
      0,
    );
  };
  const choices: Array<{ id: string | null; label: string }> = [{ id: null, label: "All" }, ...inboxFilters()];
  return (
    <div className="inbox-filters" role="group" aria-label="Show">
      {choices.map((choice) => (
        <button
          key={choice.id ?? "all"}
          type="button"
          className="btn btn--small"
          aria-pressed={choice.id === filter}
          onClick={() => onFilter(choice.id)}
        >
          {choice.label}
          {inbox === null ? null : <span className="inbox-filters__count">{count(choice.id)}</span>}
        </button>
      ))}
    </div>
  );
}

function Group({
  group,
  onOpen,
  onOlder,
}: {
  group: InboxGroup;
  onOpen: (item: InboxItem) => void;
  onOlder: (group: string, cursor: string) => void;
}) {
  const name = group.name ?? "A workspace";
  const headingId = `inbox-${groupKeyOf(group).replace(":", "-")}`;
  return (
    <section className="panel" aria-labelledby={headingId} data-testid="inbox-group">
      <div className="panel__head">
        <h2 id={headingId}>{name}</h2>
        {group.org_name && group.org_name !== group.name ? <small className="muted">{group.org_name}</small> : null}
      </div>
      {group.items.length === 0 ? null : (
        <Table caption={`Waiting in ${name}`} last="Due">
          {group.items.map((item) => (
            <Row key={`${item.kind}:${item.id}:${item.org_id ?? ""}`} item={item} group={name} testId="inbox-item">
              <button
                type="button"
                className="link inbox-subject"
                data-inbox-open={openKey(item)}
                onClick={() => onOpen(item)}
              >
                <Breakable text={rowOf(item).subject(item)} />
              </button>
              <Due at={item.expires_at} />
            </Row>
          ))}
        </Table>
      )}
      {group.answered.length === 0 ? null : (
        <Table caption={`Answered in ${name}`} last="Answer">
          {group.answered.map((item) => (
            <Row
              key={`${item.kind}:${item.id}:${item.org_id ?? ""}`}
              item={item}
              group={name}
              testId="inbox-answered"
              answered
            >
              <strong className="inbox-subject">
                <Breakable text={rowOf(item).subject(item)} />
              </strong>
              <Outcome item={item} />
            </Row>
          ))}
        </Table>
      )}
      {group.answered_next === null ? null : (
        <div className="panel__body">
          <button
            type="button"
            className="btn btn--small"
            onClick={() => onOlder(groupKeyOf(group), group.answered_next!)}
          >
            Show older
          </button>
        </div>
      )}
    </section>
  );
}

function Table({ caption, last, children }: { caption: string; last: string; children: ReactNode }) {
  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label={caption}>
      <table className="table inbox-table">
        <caption className="sr-only">{caption}</caption>
        <thead className="inbox-table__head">
          <tr>
            <th scope="col">Kind</th>
            <th scope="col">Subject</th>
            <th scope="col">Workspace</th>
            <th scope="col">Requested by</th>
            <th scope="col">{last}</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** One item's columns: `children` are the subject's own element, then the last column. */
function Row({
  item,
  group,
  testId,
  answered = false,
  children,
}: {
  item: RowItem;
  group: string;
  testId: string;
  answered?: boolean;
  children: [ReactNode, ReactNode];
}) {
  const row = rowOf(item);
  const note = row.note(item, answered);
  return (
    <tr data-testid={testId}>
      <td className="inbox-table__kind">
        <KindIcon kind={item.kind} />
      </td>
      <td>
        {children[0]}
        {note === null ? null : <small>{note}</small>}
      </td>
      <td>{item.workspace_name ?? group}</td>
      <td>{row.requestedBy(item)}</td>
      <td>{children[1]}</td>
    </tr>
  );
}

/** A subject with a break opportunity after each slash: a path breaks between its parts (#858). */
function Breakable({ text }: { text: string }) {
  const parts = text.split("/");
  return (
    <>
      {parts.map((part, at) => (
        <Fragment key={at}>
          {part}
          {at < parts.length - 1 ? (
            <>
              /<wbr />
            </>
          ) : null}
        </Fragment>
      ))}
    </>
  );
}

/** How long is left, as the invitation cards say it, with the full time on hover. */
function Due({ at }: { at: string | null }) {
  if (at === null) return <span className="muted">No deadline</span>;
  return (
    <time dateTime={at} title={readableTime(at)} className={isUrgent(at) ? "is-urgent" : undefined}>
      {deadlineOf(at)}
    </time>
  );
}

/** The selected workspace's standing grants, under Data access: what its old screen listed. */
function Standing({ consent, error, workspaceName }: { consent: ConsentRead | null; error: string | null; workspaceName: string }) {
  if (error !== null) return <Notice tone="bad">{error}</Notice>;
  if (consent === null) return null;
  return <StandingGrants grants={consent.grants} workspaceName={workspaceName} />;
}

/** The outcome, who gave it and when: the audit row's, and nothing where there is none. */
export function Outcome({ item }: { item: AnsweredItem }) {
  const { outcome, status, by, at } = item.answer;
  const text = outcomeText(item);
  return (
    <small className="muted" data-testid="inbox-outcome">
      <strong>{text}</strong>
      {status !== outcome ? <> ({status})</> : null}
      {by === null ? null : <> by {by}</>}
      {at === null ? null : <> on {whenOf(at)}</>}
    </small>
  );
}
